import LegalLayout from "./LegalLayout";

const S = ({ n, title, children, testid }) => (
  <section data-testid={testid} className="mt-12">
    <h2 className="font-display text-xl font-medium tracking-tight text-white">
      <span className="mr-3 text-sm font-light text-cyan-300/70">{n}</span>
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#C5CBD5]">{children}</div>
  </section>
);

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" updated="Last updated: October 2026" testid="terms-page">
      <S n="01" title="Who We Are" testid="terms-s1">
        <p>
          These Terms of Service are issued by AIROBOTCAMSTUDIO LTD, a company registered in the
          United Kingdom (UK Company No. 17410621) with its registered office at 71–75 Shelton
          Street, Covent Garden, London, United Kingdom, WC2H 9JQ. You can contact us at
          info@airobotcamstudio.com or airobotcamstudio@gmail.com.
        </p>
        <p>
          AIROBOTCAMSTUDIO LTD is developing an interactive AI entertainment platform combining
          real-time AI videochat, photorealistic virtual companions and conversational AI. By
          accessing this website, you agree to these Terms.
        </p>
      </S>

      <S n="02" title="Development Status of the Platform" testid="terms-s2">
        <p>
          The real-time AI videochat product (the MVP) is currently in development. The commercial
          product has not yet launched. Features, visuals and videos shown on this website may be
          concepts, pre-rendered demonstrations, prototypes or planned functionality. Commercial
          availability, functionality, pricing and launch dates may change as development
          progresses. Nothing on this website constitutes a promise that any particular feature
          will be available at any particular time.
        </p>
      </S>

      <S n="03" title="AI Characters" testid="terms-s3">
        <p>
          All characters shown or offered through the platform are AI-generated and entirely
          fictional. They are not real people, and any resemblance to real persons is unintended
          and coincidental. AI-generated responses may occasionally be inaccurate, incomplete or
          unexpected, and must not be relied upon as professional medical, legal, financial or
          other regulated professional advice.
        </p>
      </S>

      <S n="04" title="Eligibility" testid="terms-s4">
        <p>
          The future platform is intended for adults. When the service launches, you must be at
          least 18 years old (or the age of majority in your jurisdiction, if higher) to use it.
          By using this website's forms you confirm that the information you provide is accurate
          and that you are entitled to provide it.
        </p>
      </S>

      <S n="05" title="Acceptable Use" testid="terms-s5">
        <p>You agree to use this website and any future service lawfully and responsibly. You must not:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Use the service for illegal activities, abuse, harassment, fraud, impersonation or exploitation;</li>
          <li>Attempt to compromise, probe or disrupt the platform, its security or its infrastructure;</li>
          <li>Launch automated attacks, excessive automated usage or scraping where prohibited;</li>
          <li>Reverse engineer the service except where such restriction is prohibited by law;</li>
          <li>Engage in any other harmful activity or activity that materially disrupts the service for other users.</li>
        </ul>
        <p>Our Fair Use Policy forms part of these Terms.</p>
      </S>

      <S n="06" title="Intellectual Property" testid="terms-s6">
        <p>
          All intellectual property in this website, the platform, branding, software, AI character
          designs and original content remains owned by AIROBOTCAMSTUDIO LTD or its respective
          licensors. No right, title or interest is granted to you except the limited right to
          access the website for its intended purpose.
        </p>
      </S>

      <S n="07" title="Third-Party Services" testid="terms-s7">
        <p>
          The website uses Resend as part of its email and contact infrastructure. Your use of
          that service may be subject to its own terms. We are not responsible for third-party
          services outside our reasonable control.
        </p>
      </S>

      <S n="08" title="Service Availability" testid="terms-s8">
        <p>
          This website is provided on an "as is" and "as available" basis. We may suspend, withdraw
          or restrict availability for maintenance, security or development reasons. As the
          platform is in development, uninterrupted availability is not guaranteed.
        </p>
      </S>

      <S n="09" title="Investment Information and Disclaimer" testid="terms-investment">
        <p>
          This website contains information about a planned fundraising round. The round is in
          preparation and, unless this website is later explicitly updated with verified
          information, no investment has been secured. Investment amounts shown are indicative
          expressions of interest only; registering interest does not constitute an investment
          transaction, an offer to the public, investment advice or a binding invitation to
          subscribe for shares. Any actual investment will be subject to investor eligibility,
          appropriate legal documentation, company approval and applicable UK legal and regulatory
          requirements.
        </p>
        <p>
          AIROBOTCAMSTUDIO LTD is preparing an application for SEIS Advance Assurance. SEIS Advance
          Assurance has not been granted. SEIS eligibility and any investor tax relief are not
          guaranteed and are subject to HMRC requirements and individual investor circumstances.
          Capital is at risk: investments in early-stage companies are high risk and illiquid, and
          investors may lose some or all of the amount invested.
        </p>
      </S>

      <S n="10" title="Limitation of Liability" testid="terms-s10">
        <p>
          Nothing in these Terms excludes liability that cannot lawfully be excluded, including
          liability for death or personal injury caused by negligence or for fraud. Subject to
          that, and to the maximum extent permitted by law, AIROBOTCAMSTUDIO LTD excludes all
          implied warranties and is not liable for indirect, incidental or consequential losses, or
          for loss of profits, data or goodwill, arising from use of this website or reliance on
          concept or demonstration content.
        </p>
      </S>

      <S n="11" title="Suspension and Termination" testid="terms-s11">
        <p>
          We may suspend or terminate access to the website or future service where we reasonably
          believe these Terms or the Fair Use Policy have been breached, or to protect the
          platform, other users or third parties.
        </p>
      </S>

      <S n="12" title="Changes to the Service and to These Terms" testid="terms-s12">
        <p>
          As the platform is in active development, the service and these Terms may change. Updated
          Terms will be posted on this page with a revised "last updated" date. Continued use of
          the website after changes take effect constitutes acceptance of the updated Terms.
        </p>
      </S>

      <S n="13" title="Governing Law and Jurisdiction" testid="terms-s13">
        <p>
          These Terms are governed by the laws of England and Wales, and the courts of England and
          Wales have exclusive jurisdiction over any dispute arising from them, except where
          mandatory consumer protection laws in your jurisdiction provide otherwise.
        </p>
      </S>

      <S n="14" title="Contact" testid="terms-s14">
        <p>
          Questions about these Terms: info@airobotcamstudio.com or airobotcamstudio@gmail.com.
          Registered office: 71–75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ.
        </p>
      </S>
    </LegalLayout>
  );
}
